import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter, 
  Button, 
  OutlineButton, 
  Input, 
  Select, 
  TextArea, 
  FormGroup, 
  Label, 
  ErrorText, 
  Grid, 
  FlexContainer 
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { AccountingEntry, AccountingType } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormTitle = styled.h1`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin: 1.5rem 0 1rem;
`;

const FileInput = styled.div`
  margin-top: 0.5rem;
  
  input {
    display: none;
  }
  
  label {
    display: inline-block;
    padding: 0.5rem 1rem;
    background-color: var(--light-gray);
    color: var(--dark-gray);
    border-radius: var(--border-radius);
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
      background-color: var(--medium-gray);
    }
  }
`;

const InfoText = styled.p`
  color: var(--dark-gray);
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const AccountingEntryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState<{
    projectId?: string;
    description: string;
    amount: number;
    currency: string;
    type: AccountingType;
    category: string;
    account: string;
    date: string;
    reference?: string;
    attachments: File[];
  }>({
    projectId: '',
    description: '',
    amount: 0,
    currency: 'TZS',
    type: 'Expense',
    category: '',
    account: '',
    date: new Date().toISOString().split('T')[0],
    reference: '',
    attachments: []
  });
  
  const [errors, setErrors] = useState<{
    description?: string;
    amount?: string;
    category?: string;
    account?: string;
    date?: string;
  }>({});
  
  const [loading, setLoading] = useState(isEditMode);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await apiService.getProjects();
        setProjects(response.data || []);
      } catch (error: any) {
        console.error('Error loading projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };
    
    loadProjects();
  }, []);
  
  useEffect(() => {
    if (isEditMode && id) {
      // In a real application, you would fetch data from an API here
      setLoading(false);
    }
  }, [id, isEditMode]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };
  
  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };
  
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (formData.amount === 0) {
      newErrors.amount = 'Amount must be greater than zero';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.account.trim()) {
      newErrors.account = 'Account is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would submit the data to an API here
      showDialog('Success', `Accounting entry ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
      setTimeout(() => {
        navigate('/accounting');
      }, 1500);
    }
  };
  
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Get project details for display
  const selectedProject = projects.find((p: any) => p._id === formData.projectId || p.id === formData.projectId);
  
  return (
    <FormContainer>
      <FlexContainer justify="space-between" align="center" style={{ marginBottom: '1.5rem' }}>
        <OutlineButton as={Link} to="/accounting">
          <span style={{ marginRight: '8px' }}>←</span>
          Back to Accounting
        </OutlineButton>
        <FormTitle style={{ margin: 0 }}>{isEditMode ? 'Edit Accounting Entry' : 'Add Accounting Entry'}</FormTitle>
      </FlexContainer>
      
      <Card>
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <h3>Entry Information</h3>
          </CardHeader>
          <CardBody>
            <Grid columns="1fr 1fr" gap="1rem">
              <FormGroup>
                <Label htmlFor="projectId">Project (Optional)</Label>
                <Select
                  id="projectId"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  disabled={projectsLoading}
                >
                  <option value="">Select a project (optional)</option>
                  {projects.map((project: any) => (
                    <option key={project._id || project.id} value={project._id || project.id}>
                      {project.name}
                    </option>
                  ))}
                </Select>
                {selectedProject && (
                  <InfoText>
                    Budget: {formatCurrency(selectedProject.budget?.total || 0, selectedProject.budget?.currency || 'TZS')}
                  </InfoText>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="type">Entry Type *</Label>
                <Select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="Income">Income</option>
                  <option value="Expense">Expense</option>
                  <option value="Asset">Asset</option>
                  <option value="Liability">Liability</option>
                  <option value="Equity">Equity</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="amount">Amount ({formData.currency}) *</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleInputChange}
                  min="0"
                  step="1000"
                  placeholder="Enter amount"
                  className={errors.amount ? 'error' : ''}
                />
                {errors.amount && <ErrorText>{errors.amount}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                >
                  <option value="TZS">TZS</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="category">Category *</Label>
                <Input
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Office Supplies, Travel, Equipment"
                  className={errors.category ? 'error' : ''}
                />
                {errors.category && <ErrorText>{errors.category}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="account">Account *</Label>
                <Input
                  id="account"
                  name="account"
                  value={formData.account}
                  onChange={handleInputChange}
                  placeholder="e.g., Bank Account, Cash, Accounts Payable"
                  className={errors.account ? 'error' : ''}
                />
                {errors.account && <ErrorText>{errors.account}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="date">Entry Date *</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className={errors.date ? 'error' : ''}
                />
                {errors.date && <ErrorText>{errors.date}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="reference">Reference Number (Optional)</Label>
                <Input
                  id="reference"
                  name="reference"
                  value={formData.reference}
                  onChange={handleInputChange}
                  placeholder="Enter reference number"
                />
              </FormGroup>
            </Grid>
            
            <SectionTitle>Entry Details</SectionTitle>
            
            <FormGroup>
              <Label htmlFor="description">Description *</Label>
              <TextArea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the accounting entry"
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <ErrorText>{errors.description}</ErrorText>}
            </FormGroup>
            
            <SectionTitle>Attachments</SectionTitle>
            
            <FormGroup>
              <Label>Upload Documents</Label>
              <FileInput>
                <input
                  type="file"
                  id="attachments"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
                <label htmlFor="attachments">
                  Choose Files
                </label>
              </FileInput>
              <InfoText>
                Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 5MB each)
              </InfoText>
            </FormGroup>
            
            {formData.attachments.length > 0 && (
              <div>
                <h4>Selected Files:</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {formData.attachments.map((file, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'var(--light-gray)',
                      marginBottom: '0.5rem',
                      borderRadius: 'var(--border-radius)'
                    }}>
                      <span>{file.name}</span>
                      <Button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        style={{ 
                          background: 'var(--danger-color)', 
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.8rem'
                        }}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
          <CardFooter>
            <FlexContainer gap="1rem" justify="flex-end">
              <OutlineButton as={Link} to="/accounting" type="button">
                Cancel
              </OutlineButton>
              <Button type="submit">
                {isEditMode ? 'Update Entry' : 'Add Entry'}
              </Button>
            </FlexContainer>
          </CardFooter>
        </form>
      </Card>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
      
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText="OK"
        showCancel={false}
      />
    </FormContainer>
  );
};

export default AccountingEntryForm; 